import { Container } from 'aurelia-dependency-injection';
import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';

import { APIParameters, GenericApi } from '~/api/generic-api';
import {
	AutocompleteOptions,
	AutocompleteRemoteLoader,
} from '~/components/field/common/autocomplete/autocomplete-react';
import {
	FilterBy,
	SearchDefinition,
	SearchDefinitionType,
	SearchFilter,
	SearchPlainFilter,
} from '~/components/field/common/search-definition';
import { FilterType } from '~/components/filter/filter-options';
import { Notification } from '~/components/notification/notification';
import { FieldModel, FormModel } from '~/models/form-model';
import { SearchResponse } from '~/models/search-response';
import {
	AcurityFieldUtils,
	FieldNameParamType,
} from '~/utils/acurity-field-utils';
import { FetchUtils } from '~/utils/fetch-utils';
import FormModelUtils from '~/utils/form-model-utils';
import { Logger } from '~/utils/logger';
import { ObjectUtils } from '~/utils/utils';

const MaxSearchLength = 20;

export interface AcuritySearchApiOptions {
	/**
	 * name is the field name that owns the options.
	 * This was introduced to improve debugging bugs with acurity-search-api.
	 */
	name: string;
	searchDefinition?: SearchDefinition;
	additionalFilters?: SearchFilter[];
	filterBy: string[] | FilterBy[];
	formModel: FormModel;
}

interface SearchFilterResult<T> {
	data: T;
	fieldsWithNoValue: { [columnName: string]: FieldModel };
}

@autoinject
export class AcuritySearchApi
	extends GenericApi
	implements AutocompleteRemoteLoader
{
	public getResults = (
		query: string,
		options: AutocompleteOptions,
	): Promise<any[]> | undefined => {
		if (!query && !options.allowEmptyQuery) {
			return undefined;
		}
		Logger.debug(`Getting search results for query: "${query}"`);
		const remoteOptions: AcuritySearchApiOptions = options.remoteOptions;
		if (!remoteOptions.searchDefinition) {
			throw new Error('Missing "searchDefinition" from <a-search>.');
		}

		switch (remoteOptions.searchDefinition.searchType) {
			case SearchDefinitionType.DATATABLE: {
				const result = AcuritySearchApi.getDataTableSearchFilters(
					query.trim(),
					remoteOptions,
				);
				if (Object.keys(result.fieldsWithNoValue).length > 0) {
					AcuritySearchApi.showNotificationAboutDependentFields(
						result.fieldsWithNoValue,
					);
					return Promise.resolve([]);
				}
				const params: { [paramName: string]: any } = {
					columns:
						remoteOptions.searchDefinition.columnDefinition.map(
							(colDef) => ({ data: colDef.name }),
						),
					order: [
						{
							column: 0,
							dir: 'asc',
						},
					],
					filters: result.data,
					length: MaxSearchLength,
				};

				// add fields required for description if they are not included in requested columns
				for (const descriptionField of remoteOptions.searchDefinition
					.descriptionDefinition) {
					if (
						!params.columns.some(
							(column) => column.data === descriptionField,
						)
					) {
						params.columns.push({ data: descriptionField });
					}
				}

				return FetchUtils.getJSON('api/:functionName', {
					functionName: remoteOptions.searchDefinition.resourcePath,
					...params,
				}).then(
					(response: {
						data: Array<{ [fieldName: string]: any }>;
						descriptionMap: { [fieldName: string]: any };
					}) => {
						const results: Array<{ [fieldName: string]: string }> =
							[];
						if (response.data.length > 0) {
							for (const record of response.data) {
								for (const fieldName of Object.keys(record)) {
									const fieldDescriptions =
										response.descriptionMap[fieldName];
									if (
										remoteOptions.searchDefinition
											.rowIdKey !== fieldName &&
										fieldDescriptions &&
										fieldDescriptions.codeMap &&
										fieldDescriptions.codeMap[
											record[fieldName].trim()
										]
									) {
										const description =
											fieldDescriptions.codeMap[
												record[fieldName].trim()
											].description;
										record[fieldName] +=
											ObjectUtils.notEmpty(description)
												? ' (' + description + ')'
												: '';
									}
								}
								results.push(record);
							}
						}
						return results;
					},
				);
			}

			case SearchDefinitionType.SEARCH: {
				const params = {
					q: query,
				};
				const fieldTypes =
					options.fieldTypes ?? AcurityFieldUtils.defaultFieldTypes;
				const result = AcuritySearchApi.applySearchFilters(
					params,
					remoteOptions,
					fieldTypes,
				);
				if (Object.keys(result.fieldsWithNoValue).length > 0) {
					AcuritySearchApi.showNotificationAboutDependentFields(
						result.fieldsWithNoValue,
					);
					return Promise.resolve([]);
				}
				return FetchUtils.getJSON('api/:functionName/search', {
					functionName: remoteOptions.searchDefinition.resourcePath,
					'field-model': true,
					...params,
				}).then((response: SearchResponse<any>) => {
					const results: Array<{ [fieldName: string]: string }> = [];
					if (response.data.length > 0) {
						const values = response.data[0].values;
						if (values && typeof values === 'object') {
							// NOTE: Jake: 2019-10-09
							// Handle data that has "values" + optional "fieldModel", ie. InvestmentManagerResource
							// Flatten it so it *just works* with the Autocomplete component.
							for (const record of response.data) {
								for (const fieldName of Object.keys(
									record.values,
								)) {
									const fieldModel = record.fieldModel;
									// NOTE(Albert): 2020-04-07 - WEBUI-3141
									// Do not add description to value if column is id key of search
									// field because this is used to set value of input on selection.
									// I don't think there are any cases where id key would have a
									// description, but added this check just in case.
									if (
										remoteOptions.searchDefinition
											.rowIdKey !== fieldName &&
										fieldModel &&
										fieldModel[fieldName] &&
										typeof fieldModel[fieldName]
											.fieldDescription === 'string'
									) {
										const description =
											fieldModel[fieldName]
												.fieldDescription;
										record.values[fieldName] +=
											ObjectUtils.notEmpty(description)
												? ' (' + description + ')'
												: '';
									}
								}
								results.push(record.values);
							}
						} else {
							// NOTE: Jake: 2019-10-09
							// Handle data that's flat and simple, like the QasResource
							// data.
							results.push(...response.data);
						}
					}
					return results;
				});
			}
		}
	};

	/**
	 * showNotificationAboutDependentFields when a value is required.
	 *
	 * Ideally we should propogate this message into the <a-search> validation message but
	 * that effort is a lot more than just showing a notification, so we're opting for this approach for now.
	 */
	private static showNotificationAboutDependentFields(fields: {
		[columnName: string]: FieldModel;
	}) {
		let fieldStr = '';
		for (const columnName in fields) {
			if (!fields.hasOwnProperty(columnName)) {
				continue;
			}
			if (fieldStr !== '') {
				fieldStr += ', ';
			}
			const fieldModel = fields[columnName];
			fieldStr += fieldModel.label;
		}
		Notification.info(
			Container.instance
				.get(I18N)
				.tr('errors.search-dependent-need-value', { fields: fieldStr }),
		);
	}

	/**
	 * Gets the filter if filterBy is set, else return an empty list
	 *
	 * NOTE: Order of values being set via `filtersMap.set` is IMPORTANT
	 */
	private static getDataTableSearchFilters = (
		query: string,
		options: AcuritySearchApiOptions,
	): SearchFilterResult<SearchFilter[]> => {
		if (
			options.searchDefinition.searchType !==
			SearchDefinitionType.DATATABLE
		) {
			throw new Error(
				'getSearchFilters can only used by SearchDefinitionType.DATATABLE.',
			);
		}

		const filtersMap = new Map<string, SearchFilter>();
		const fieldsWithNoValue: { [columnName: string]: FieldModel } = {};

		// Get default filters from `searchDefinition.columnDefinition`
		// Also, set the value based on the current `fieldModel`
		for (const column of options.searchDefinition.columnDefinition) {
			if (column.excludeFromFilter) {
				continue;
			}
			const name = column.name;
			// NOTE: Jake: 2019-12-19
			// This can be made more robust so that we just read dataType information
			// from each column rather than inferring based on the column name.
			const isNumber = FormModelUtils.isFieldTypeNumeric(name);
			if (isNumber && query !== String(Number(query))) {
				// NOTE: Jake: 2019-12-19 - WEBUI-2796
				// If column is a number data type but the query cant be cleanly
				// cast to a number then ignore filtering that column as we get errors
				// otherwise.
				continue;
			}
			filtersMap.set(column.name, {
				operation: FilterType.STRING_STARTS_WITH,
				not: false,
				ignoreCase: true,
				andJoin: false,
				data: column.name,
				value: query,
			});
		}

		// Setup default search filters and "additional-filter"
		const filters: SearchFilter[] = [];
		if (
			options.searchDefinition.filters &&
			options.searchDefinition.filters.length > 0
		) {
			// Get filters from the search definition
			filters.push(...options.searchDefinition.filters);
		}
		if (options.additionalFilters && options.additionalFilters.length > 0) {
			// Get additional filters that were applied to the <search> field directly
			filters.push(...options.additionalFilters);
		}
		for (const filter of filters) {
			if (!filter && !String(filter.value).trim()) {
				continue;
			}
			const newFilter = {
				...filtersMap.get(filter.name), // Default filters set above
				data: filter.name, // Because `name` is intended for `data`
				...filter,
			};
			delete newFilter.name; // remove "name" as we already have it on the "data" property now.
			filtersMap.set(newFilter.data, newFilter);
		}

		// Allow filtering by another form fields value
		if (options.filterBy && options.filterBy.length > 0) {
			if (!options.searchDefinition) {
				throw new Error('Missing "searchDefinition".');
			}
			// NOTE: Jake: 2018-12-13 - WEBUI-1304
			// This code used to exist in search.ts before the refactor.
			// I accidentally forgot to re-introduce it / test it until WEBUI-1304.
			if (!options.formModel) {
				throw new Error(
					'Cannot search, missing "formModel" on field: ' +
						options.name,
				);
			}
			for (const filterByObj of options.filterBy) {
				let filterByCol: string;
				let filterBy: string;
				if (typeof filterByObj === 'object') {
					filterBy = filterByObj.col;
					filterByCol = filterByObj.targetCol;
				} else {
					filterBy = filterByObj;
					// filterBy column can be in columnDefinition, filters or additionalFilters
					filterByCol = Array.from(filtersMap.keys()).find((key) =>
						key.endsWith(filterBy.substring(2)),
					);
				}
				const fieldModel = options.formModel.fields[filterBy];
				if (!fieldModel) {
					throw new Error(
						'Cannot filter by field: "' +
							filterBy +
							'". Does not exist on this form.',
					);
				}
				if (!filterByCol) {
					throw new Error(
						'Empty "filterByCol" not allowed for field: ' +
							fieldModel.name +
							', Is this column defined on the "filters" property for this search-definition?',
					);
				}
				const value = fieldModel.value.trim();
				if (!value) {
					// for filters with `ignoreBlank` we only want to add field to filter map if it has a value
					if (
						typeof filterByObj === 'object' &&
						filterByObj.ignoreBlank
					) {
						filtersMap.delete(filterByCol);
					} else {
						fieldsWithNoValue[fieldModel.name] = fieldModel;
					}
					continue;
				}
				const existingFilterObj = filtersMap.get(filterByCol);
				if (!existingFilterObj) {
					throw new Error(
						'Missing "\'' +
							filterByCol +
							'\'" in "filters" array for search definition.',
					);
				}
				filtersMap.set(filterByCol, {
					...existingFilterObj,
					value,
					andJoin: true,
				});
			}
		}

		return {
			data: [...Array.from(filtersMap.values())],
			fieldsWithNoValue,
		};
	};

	/**
	 * applySearchFilters will update the "params" argument with filter values.
	 *
	 * If true is returned, then cancel the search request and return an empty list.
	 */
	private static applySearchFilters(
		params: APIParameters,
		options: AcuritySearchApiOptions,
		fieldTypes?: readonly FieldNameParamType[],
	): SearchFilterResult<APIParameters> {
		if (
			options.searchDefinition.searchType !== SearchDefinitionType.SEARCH
		) {
			throw new Error(
				'getSearchFilters can only used by SearchDefinitionType.SEARCH.',
			);
		}

		const fieldsWithNoValue: { [columnName: string]: FieldModel } = {};
		if (options.searchDefinition.filters) {
			for (const filter of options.searchDefinition.filters) {
				if (!filter.value) {
					// These are filters with hardcoded values.
					// Not inferred from other fields.
					continue;
				}
				let paramName: string = filter.paramName;
				if (!paramName) {
					paramName = filter.name;
				}
				params[paramName] = filter.value;
			}
		}
		if (options.additionalFilters) {
			for (const filter of options.additionalFilters) {
				if (filter.operation !== FilterType.EQUALS) {
					throw new Error(
						'Can only use FilterType.EQUALS when using additionalFilters on SearchDefinitionType.SEARCH.',
					);
				}
				if (filter.andJoin !== true) {
					throw new Error(
						'Can only use "andJoin = true" when using additionalFilters on SearchDefinitionType.SEARCH.',
					);
				}
				params[filter.name] = filter.value;
			}
		}
		if (options.filterBy && options.filterBy.length > 0) {
			if (!options.searchDefinition) {
				throw new Error('Missing "searchDefinition".');
			}
			if (!options.formModel) {
				throw new Error(
					'Cannot search, missing "formModel" on field: ' +
						options.name,
				);
			}
			for (let columnName of options.filterBy) {
				// this queryParamName variable seems not have any usages
				let queryParamName: string;
				let ignoreIfNoValue: boolean = false;
				if (typeof columnName !== 'string') {
					const info = columnName;
					columnName = info.col;
					queryParamName = info.targetCol;
					ignoreIfNoValue = info.ignoreBlank ? true : false;
				} else {
					queryParamName = columnName;
				}
				const fieldModel = options.formModel.fields[columnName];
				if (!fieldModel) {
					throw new Error(
						'Cannot filter by field: "' +
							columnName +
							'". Does not exist on this form.',
					);
				}
				let filter: SearchPlainFilter | undefined;
				if (options.searchDefinition.filters) {
					for (const colDef of options.searchDefinition.filters) {
						if (colDef.name === columnName) {
							filter = colDef;
						}
					}
				}

				// If a specific filter cannot be found, try to infer a generic one for this field from the given field
				// types.
				if (!filter && fieldTypes && fieldModel.name) {
					const fieldParam = fieldTypes.find((fieldType) =>
						fieldModel.name.endsWith(fieldType.fieldNameEndsWith),
					);
					if (fieldParam) {
						filter = {
							name: fieldModel.name,
							paramName: fieldParam.paramType,
						};
					}
				}

				if (!filter) {
					throw new Error(
						'Empty "filter" not allowed for field: ' +
							fieldModel.name +
							', Is this column defined on the "filters" property for this search-definition?',
					);
				}
				const value = fieldModel.value.trimRight();
				if (!value) {
					if (ignoreIfNoValue) {
						// Skip adding to "fieldsWithNoValue" as this field is not required
						continue;
					}
					fieldsWithNoValue[fieldModel.name] = fieldModel;
					continue;
				}
				let paramName: string = filter.paramName;
				if (!paramName) {
					paramName = filter.name;
				}
				params[paramName] = value;
			}
		}
		return {
			data: params,
			fieldsWithNoValue,
		};
	}

	public getSearchDescription = (
		value: string,
		searchDefinition: SearchDefinition,
		additionalFilters: SearchFilter[] = [],
	): Promise<string> => {
		let searchFilters: SearchFilter[] = [
			{
				operation: FilterType.EQUALS,
				not: false,
				ignoreCase: true,
				andJoin: false,
				data: searchDefinition.rowIdKey,
				value,
			},
			...additionalFilters,
		];

		// Filter empty filters and set name to data
		const newSearchFilters: SearchFilter[] = [];
		for (const filter of searchFilters) {
			if (!filter && !String(filter.value).trim()) {
				continue;
			}
			const newFilter = {
				data: filter.name, // Because `name` is intended for `data`
				...filter,
			};
			delete newFilter.name; // remove "name" as we already have it on the "data" property now.
			newSearchFilters.push(newFilter);
		}
		searchFilters = newSearchFilters;

		const params: { [paramName: string]: any } = {
			columns: searchDefinition.descriptionDefinition.map(
				(fieldDefinition) => ({ data: fieldDefinition }),
			),
			filters: searchFilters,
			length: 1,
		};
		return FetchUtils.getJSON('api/:functionName', {
			functionName: searchDefinition.resourcePath,
			...params,
		}).then((response: { data: Array<{ [fieldName: string]: any }> }) => {
			const descArray =
				response && response.data && response.data.length > 0
					? Object.values(response.data[0])
					: [''];
			return AcurityFieldUtils.descriptionFromArray(descArray);
		});
	};
}
